import { Component, OnInit } from '@angular/core';
import { DynamicDialogConfig } from 'primeng/dynamicdialog';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';

import { formatDateAsYyyyMmDdLocal } from '@app/core/utils/format-local-iso-date';
import { PatientRequestsService, IPatientHistory } from '@app/core/patient-requests';
import { UsersService } from '@app/core/users';
import { ShoppingCartItem } from '@app/shared/select-shopping-cart/select-shopping-cart.component';

export interface AcumuladosSearchDialogData {
  patientId: string;
  patient?: IPatientHistory | null;
  /** Lab options for dropdown: label = Laboratorio name, value = same, labNr optional if known */
  labOptions: { label: string; value: string; labNr?: number }[];
}

export interface PatientTestItem {
  methNr: number;
  methName: string;
}

@Component({
  selector: 'afoone-acumulados-search-dialog',
  templateUrl: './acumulados-search-dialog.component.html',
  styleUrls: ['./acumulados-search-dialog.component.css'],
})
export class AcumuladosSearchDialogComponent implements OnInit {
  patientId = '';
  labOptions: { label: string; value: string; labNr?: number }[] = [];
  selectedLab: { label: string; value: string; labNr?: number } | null = null;
  referenceDate: Date = new Date();
  minDate = new Date(1900, 0, 1);
  maxDate = new Date();

  availableTests: PatientTestItem[] = [];
  /** Items for the left list of select-shopping-cart (all loaded tests as ShoppingCartItem) */
  availableItemsForCart: ShoppingCartItem[] = [];
  /** Bound to select-shopping-cart; derived selectedTests for API from this */
  selectedTestsForCart: ShoppingCartItem[] = [];
  /** true = panel plegado (lista oculta), WIR-RF-03.02.3.1. Usar [(collapsed)] con PrimeNG, no solo onAfterToggle. */
  technicasPanelCollapsed = true;
  panelTechnicasDisabled = true;
  /** First time panel expanded for current lab - need to load tests */
  panelNeverLoadedForCurrentLab = true;

  loadingTests = false;
  loadingSearch = false;
  errorMessage: string | null = null;

  constructor(
    private config: DynamicDialogConfig,
    private service: PatientRequestsService,
    private userService: UsersService,
    private router: Router,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    const data = this.config.data as AcumuladosSearchDialogData | undefined;
    if (data) {
      // wspi_Search_Cumu exige el NHC con asterisco como primer carácter; no quitarlo
      this.patientId = (data.patientId || '').trim();
      if (!this.patientId && data.patient?.historia) {
        this.patientId = String(data.patient.historia).trim();
      }
      this.labOptions = data.labOptions || [];
      if (this.labOptions.length === 1) {
        this.selectedLab = this.labOptions[0];
        this.panelTechnicasDisabled = false;
      }
    }
  }

  /** Fecha “Anteriores a” obligatoria: si queda vacía, restaurar a hoy. */
  onReferenceDateModelChange(value: Date | null): void {
    if (value == null || Number.isNaN(value.getTime())) {
      this.referenceDate = new Date();
    }
  }

  get canSearchTable(): boolean {
    return this.selectedLab != null && !this.loadingSearch;
  }

  /** Gráficas de evolución: el SP/UI solo admite 1–10 pruebas (WIR-RF-03.01.4 / issue #494). */
  get canGraficasEvolucion(): boolean {
    const n = this.selectedTestsForCart?.length ?? 0;
    return n >= 1 && n <= 10;
  }

  /** Selected techniques for API calls, derived from cart selection */
  get selectedTests(): PatientTestItem[] {
    return this.selectedTestsForCart.map((s) => ({
      methNr: s.id as number,
      methName: s.label,
    }));
  }

  onLabChange(): void {
    if (!this.selectedLab) {
      this.panelTechnicasDisabled = true;
      this.technicasPanelCollapsed = true;
      this.availableTests = [];
      this.availableItemsForCart = [];
      this.selectedTestsForCart = [];
      this.errorMessage = null;
      return;
    }
    this.panelTechnicasDisabled = false;
    this.technicasPanelCollapsed = true;
    this.availableTests = [];
    this.availableItemsForCart = [];
    this.selectedTestsForCart = [];
    this.errorMessage = null;
    this.panelNeverLoadedForCurrentLab = true;
  }

  /** Estado del p-panel (PrimeNG emite collapsedChange; evitar [collapsed] unidireccional + onAfterToggle, que replegan el panel). */
  onTechnicasPanelCollapsedChange(collapsed: boolean): void {
    this.technicasPanelCollapsed = collapsed;
    if (collapsed || !this.selectedLab) return;
    if (this.panelNeverLoadedForCurrentLab) {
      this.loadPatientTests();
    }
  }

  private getLabNrForSelectedLab(): number | null {
    if (!this.selectedLab) return null;
    if (this.selectedLab.labNr != null) return this.selectedLab.labNr;
    return null;
  }

  private resolveLabNr(cb: (labNr: number) => void): void {
    const labNr = this.getLabNrForSelectedLab();
    if (labNr != null) {
      cb(labNr);
      return;
    }
    const labName = this.selectedLab?.value ?? this.selectedLab?.label;
    if (!labName) {
      this.errorMessage = this.translate.instant('ACUMULADOS.DB_ERROR_SEARCHING');
      return;
    }
    this.service.getLabNrByLaborName(labName).subscribe({
      next: (res) => {
        if (res.labNr == null) {
          this.errorMessage = this.translate.instant('ACUMULADOS.DB_ERROR_SEARCHING');
        } else {
          if (this.selectedLab) this.selectedLab = { ...this.selectedLab, labNr: res.labNr };
          cb(res.labNr);
        }
      },
      error: () => {
        this.errorMessage = this.translate.instant('ACUMULADOS.DB_ERROR_SEARCHING');
      },
    });
  }

  loadPatientTests(): void {
    if (!this.selectedLab || !this.patientId) return;
    this.errorMessage = null;
    this.resolveLabNr((labNr) => {
      this.loadingTests = true;
      this.service.getPatientTests(this.patientId, labNr).subscribe({
        next: (res) => {
          this.loadingTests = false;
          this.panelNeverLoadedForCurrentLab = false;
          if (res.msgType !== 0 && res.msgCode) {
            this.errorMessage = this.translate.instant('ACUMULADOS.' + res.msgCode);
            this.availableTests = [];
            this.availableItemsForCart = [];
          } else {
            this.availableTests = res.data || [];
            this.availableItemsForCart = this.availableTests.map((t) => ({
              id: t.methNr,
              label: t.methName,
              value: t,
            }));
          }
        },
        error: () => {
          this.loadingTests = false;
          this.panelNeverLoadedForCurrentLab = true;
          this.errorMessage = this.translate.instant('ACUMULADOS.DB_ERROR_SEARCHING');
          this.availableTests = [];
          this.availableItemsForCart = [];
        },
      });
    });
  }

  getReferenceDateForApi(): string {
    const d = this.referenceDate;
    if (!d || Number.isNaN(d.getTime())) return formatDateAsYyyyMmDdLocal(new Date());
    return formatDateAsYyyyMmDdLocal(d);
  }

  buscar(): void {
    if (!this.canSearchTable || !this.selectedLab) return;
    this.errorMessage = null;
    const testList = this.selectedTests.length
      ? this.selectedTests.map((t) => t.methNr).join(',')
      : '';
    this.resolveLabNr((labNr) => {
      this.loadingSearch = true;
      const centroId = this.userService.getSisifoDefaultCenter();
      this.service
        .searchAcumulados({
          patientId: this.patientId,
          labNr,
          referenceDate: this.getReferenceDateForApi(),
          requisType: 0,
          testList,
          graphicFormat: 0,
          centroId: centroId ?? undefined,
        })
        .subscribe({
          next: (res: any) => {
            this.loadingSearch = false;
            const data = Array.isArray(res) ? res : res?.data ?? [];
            const msgType = res?.msgType ?? 0;
            const msgCode = res?.msgCode;
            if (!data.length && msgType !== 0 && msgCode) {
              this.errorMessage = this.translate.instant('ACUMULADOS.' + msgCode);
              return;
            }
            this.openAcumuladosView(labNr, testList);
          },
          error: (err) => {
            this.loadingSearch = false;
            this.errorMessage = err?.message || err?.error?.message || this.translate.instant('ACUMULADOS.ERROR_BUSCAR');
          },
        });
    });
  }

  graficas(): void {
    const n = this.selectedTests?.length ?? 0;
    if (n < 1 || n > 10) {
      this.errorMessage = this.translate.instant('ACUMULADOS.CUMULATIVE_SELECT_TESTS');
      return;
    }
    if (!this.selectedLab) return;
    this.errorMessage = null;
    const testList = this.selectedTests.map((t) => t.methNr).join(',');
    this.resolveLabNr((labNr) => {
      this.loadingSearch = true;
      const centroId = this.userService.getSisifoDefaultCenter();
      this.service
        .searchAcumulados({
          patientId: this.patientId,
          labNr,
          referenceDate: this.getReferenceDateForApi(),
          requisType: 0,
          testList,
          graphicFormat: 1,
          centroId: centroId ?? undefined,
        })
        .subscribe({
          next: (res: any) => {
            this.loadingSearch = false;
            const data = Array.isArray(res) ? res : res?.data ?? [];
            const msgType = res?.msgType ?? 0;
            const msgCode = res?.msgCode;
            if (!data.length && msgType !== 0 && msgCode) {
              this.errorMessage = this.translate.instant('ACUMULADOS.' + msgCode);
              return;
            }
            this.openEvolutionCharts(labNr, testList);
          },
          error: (err) => {
            this.loadingSearch = false;
            this.errorMessage = err?.message || err?.error?.message || this.translate.instant('ACUMULADOS.ERROR_BUSCAR');
          },
        });
    });
  }

  private openAcumuladosView(labNr: number, testList: string): void {
    const centroId = this.userService.getSisifoDefaultCenter();
    const q: Record<string, string | number | undefined> = {
      patientId: this.patientId,
      labNr,
      referenceDate: this.getReferenceDateForApi(),
      requisType: 0,
      testList: testList || undefined,
    };
    if (centroId != null) q['centroId'] = centroId;
    const tree = this.router.createUrlTree(['/patient-requests', 'acumulados-view'], {
      queryParams: q,
      queryParamsHandling: '',
    });
    const url = this.router.serializeUrl(tree);
    const full = globalThis.location.origin + (url.startsWith('/') ? url : '/' + url);
    globalThis.open(full, '_blank');
  }

  private openEvolutionCharts(labNr: number, testList: string): void {
    const centroId = this.userService.getSisifoDefaultCenter();
    const q: Record<string, string | number | undefined> = {
      patientId: this.patientId,
      labNr,
      referenceDate: this.getReferenceDateForApi(),
      requisType: 0,
      testList,
    };
    if (centroId != null) q['centroId'] = centroId;
    const tree = this.router.createUrlTree(['/patient-requests', 'evolution-charts'], {
      queryParams: q,
      queryParamsHandling: '',
    });
    const url = this.router.serializeUrl(tree);
    const full = globalThis.location.origin + (url.startsWith('/') ? url : '/' + url);
    globalThis.open(full, '_blank');
  }
}
